import hotkeys from 'hotkeys-js';
import * as React from 'react';
import { shallowEqualArrays } from 'shallow-equal';

import { Logger } from '@gitbook/util-debug';
import { useLiveRef, useStable } from '@gitbook/util-react-hooks';

import type { ShortcutScope } from './scope';

const logger = Logger('app:keyboard');

export type KeyboardShortcuts = { [key: string]: (event: Event, ...args: any[]) => void | boolean };

/**
 * Allow keyboard shortcuts in the editor.
 */
hotkeys.filter = (event: Event) => {
    const element = event.target || event.srcElement;

    if (!(element instanceof HTMLElement)) {
        return false;
    }

    if (element.isContentEditable) {
        return true;
    }

    const { tagName } = element;
    return !(tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA');
};

/**
 * Return a stable array.
 */
function useStableArray<T>(array: T[]): T[] {
    return useStable<T[]>(array, shallowEqualArrays);
}

/**
 * Bind keyboard shortcuts.
 */
export function useKeyboardShortcuts(
    shortcuts: KeyboardShortcuts,
    scope: ShortcutScope | ShortcutScope[]
) {
    const scopes = useStableArray(Array.isArray(scope) ? scope : [scope]);
    const keys = useStableArray(Object.keys(shortcuts));
    const shortcutsRef = useLiveRef(shortcuts);

    React.useEffect(() => {
        keys.forEach((shortcut) => {
            scopes.forEach((scope) => {
                logger.debug(`bind shortcut ${shortcut} to scope ${scope}`);
                hotkeys(shortcut, scope, (event, ...args) => {
                    event.preventDefault();
                    shortcutsRef.current[shortcut](event, ...args);
                });
            });
        });

        return () => {
            keys.forEach((shortcut) => {
                scopes.forEach((scope) => {
                    logger.debug(`unbind shortcut ${shortcut} to scope ${scope}`);
                    hotkeys.unbind(shortcut, scope);
                });
            });
        };
    }, [scopes, keys, shortcutsRef]);
}
